import { useMemo } from 'react'
import { useAppState } from '@/app/providers/AppStateProvider'
import { Icon } from '@/shared/ui/Icon'
import { ModelCard } from './ModelCard'
import { SourceList } from './SourceList'
import { mockModel } from '../mock'
import {
  DRAFT_CONVERSATION_ID,
  useConversations,
} from '@/features/conversations/ConversationsProvider'
import { useAuth } from '@/features/auth'
import { useScope, multiCaseSummary } from '@/features/scope'

export function RightEvidencePanel() {
  const { toggleRight } = useAppState()
  const {
    activeConversationId,
    messagesByConversation,
    selectedAssistantMessageId,
  } = useConversations()
  const { organization } = useAuth()
  // Step 13.7 — reflete a seleção multi-doc real (selectedCaseDocuments), não o
  // legado ``activeScope`` single-doc, que fica null no modo multi-doc e fazia
  // o painel mostrar escopo errado.
  const { selectedCaseDocuments } = useScope()

  const isHome = activeConversationId === DRAFT_CONVERSATION_ID

  const selectedMessage = useMemo(() => {
    if (isHome) return null
    const messages = messagesByConversation[activeConversationId] ?? []
    if (selectedAssistantMessageId) {
      return messages.find((m) => m.id === selectedAssistantMessageId) ?? null
    }
    // fall back to the most recent assistant message
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i]!
      if (m.role === 'assistant') return m
    }
    return null
  }, [
    isHome,
    activeConversationId,
    messagesByConversation,
    selectedAssistantMessageId,
  ])

  return (
    <aside className="aside" data-testid="evidence-panel">
      <div className="aside-top">
        <button type="button" className="aside-collapse" title="Recolher painel" onClick={toggleRight}>
          <Icon name="panel-right" />
        </button>
      </div>

      <ModelCard model={mockModel} />

      <ActiveScopeStrip
        summary={multiCaseSummary(selectedCaseDocuments)}
        hasScope={selectedCaseDocuments.length > 0}
      />

      {isHome ? (
        <EvidenceEmptyState
          title="As fontes recuperadas aparecerão aqui após uma consulta."
          subtitle="Cada afirmação será rastreável até o documento e o trecho de origem."
        />
      ) : !selectedMessage ? (
        <EvidenceEmptyState
          title="Selecione uma resposta para ver suas fontes."
          subtitle="Clique no balão da resposta no chat para inspecionar os trechos."
        />
      ) : (
        <SourceList sources={selectedMessage.sources} />
      )}

      <div className="aside-foot">
        <span className="pulse" aria-hidden="true" />
        {organization
          ? `Conectado · tenant `
          : 'Sessão sem organização'}
        {organization && <b>{organization.tenant_id}</b>}
      </div>
    </aside>
  )
}

interface EvidenceEmptyStateProps {
  title: string
  subtitle: string
}

interface ActiveScopeStripProps {
  summary: string
  hasScope: boolean
}

function ActiveScopeStrip({ summary, hasScope }: ActiveScopeStripProps) {
  return (
    <div className="mx-[18px] mt-2 mb-3 rounded-lg border border-white/[0.06] bg-white/[0.025] px-3 py-2">
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.12em] text-text-mute">
        <Icon
          name={hasScope ? 'doc' : 'grid'}
          size={11}
          className={hasScope ? 'text-violet' : 'text-text-mute'}
        />
        Escopo ativo
      </div>
      <div
        className={
          'mt-1 truncate text-[12px] ' +
          (hasScope ? 'text-text' : 'text-text-mute')
        }
        title={summary}
      >
        {summary}
      </div>
    </div>
  )
}

function EvidenceEmptyState({ title, subtitle }: EvidenceEmptyStateProps) {
  return (
    <div className="mx-[18px] mt-1 mb-4 rounded-xl border border-white/[0.07] bg-white/[0.02] p-5 text-center backdrop-blur-sm">
      <div
        className="mx-auto mb-3 grid h-9 w-9 place-items-center rounded-lg border border-white/[0.08] bg-white/[0.03] text-violet"
        aria-hidden="true"
      >
        <Icon name="sparkles" size={16} />
      </div>
      <p className="m-0 text-[12.5px] leading-[1.55] text-text-soft">{title}</p>
      <p className="m-0 mt-2 text-[11px] leading-[1.5] text-text-mute">{subtitle}</p>
    </div>
  )
}
